import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  GetApp as GetAppIcon,
  AttachMoney as AttachMoneyIcon,
  CompareArrows as CompareArrowsIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import SurchargeReportBody from '../components/SurchargeReportBody';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { transactionTypeColor } from '../utils/transactionTypes';

const SettlementReports = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [reportType, setReportType] = useState('settlement'); // 'settlement' or 'transactions'
  // Transactions EXPORT only: include per-location subtotals + grand totals (and net summary).
  // Default ON (current behavior). Off = headers + data rows only, for data analysis.
  // Affects the CSV exports ONLY — the on-screen report always shows subtotals/totals.
  const [includeExportTotals, setIncludeExportTotals] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM')); // Current month by default
  const [period, setPeriod] = useState('daily'); // 'monthly', 'weekly', or 'daily'

  const defaultPeriods = { settlement: 'daily', transactions: 'daily', surcharge: 'monthly' };
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';

  const handleReportTypeChange = (value) => {
    setReportType(value);
    setPeriod(defaultPeriods[value] || 'daily');
    setReportData(null);
  };

  // Shared style for native select elements (avoids MUI Select popover/focus bugs)
  const nativeSelectSx = {
    width: '100%',
    height: '40px',
    padding: '8px 12px',
    fontSize: '14px',
    borderRadius: '4px',
    border: '1px solid rgba(0,0,0,0.23)',
    backgroundColor: '#fff',
    marginBottom: '16px',
    '&:focus': { outline: '2px solid #1976d2', borderColor: '#1976d2' },
  };

  // Default to yesterday's date for settlement reports
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };
  const [selectedDate, setSelectedDate] = useState(getYesterday());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [expandedPhysical, setExpandedPhysical] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Hierarchy state for market/store grouping
  const [markets, setMarkets] = useState([]);
  const [storesByMarket, setStoresByMarket] = useState({});
  const [expandedMarkets, setExpandedMarkets] = useState({});
  const [ungroupedLocations, setUngroupedLocations] = useState([]);

  // Report level selection: 'channel', 'market', or 'store'
  const [reportLevel, setReportLevel] = useState('store');
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [channels, setChannels] = useState([]);
  const [storesByChannel, setStoresByChannel] = useState({});

  const { getLocations, getSettlementReport, apiCall, getHierarchyGrouped } = useApi();
  
  // Generate month options for the dropdown (last 12 months)
  const generateMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      options.push({
        value: format(date, 'yyyy-MM'),
        label: format(date, 'MMMM yyyy')
      });
    }
    return options;
  };
  
  const monthOptions = generateMonthOptions();

  // Load available locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);

      // Fetch both hierarchy data and flat locations
      const [hierarchyData, locationsResponse] = await Promise.all([
        getHierarchyGrouped().catch(() => null),
        getLocations()
      ]);

      // Extract unique location names (keep for backwards compatibility)
      const uniqueLocations = [...new Set((locationsResponse || []).map(loc => loc.LocationName))].sort();
      setLocations(uniqueLocations);

      // Set up hierarchy data if available
      if (hierarchyData && hierarchyData.markets && hierarchyData.markets.length > 0) {
        setMarkets(hierarchyData.markets);
        setStoresByMarket(hierarchyData.grouped);

        // Store channel (organization) data
        if (hierarchyData.channels) {
          setChannels(hierarchyData.channels);
          setStoresByChannel(hierarchyData.storesByChannel || {});
        }

        // Find locations that aren't in any market (ungrouped).
        // Match by NORMALIZED name (strip punctuation/spacing/case) so a settlement-only
        // spelling like "Rise Erie - Lake" folds into the hierarchy store "Rise Erie Lake"
        // instead of appearing as a duplicate entry under "Other Locations".
        const normLoc = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
        const groupedNorm = new Set();
        Object.values(hierarchyData.grouped).forEach(stores => {
          stores.forEach(store => groupedNorm.add(normLoc(store)));
        });
        const ungrouped = uniqueLocations.filter(loc => !groupedNorm.has(normLoc(loc)));
        setUngroupedLocations(ungrouped);

        // Expand all markets by default
        const expanded = {};
        hierarchyData.markets.forEach(market => {
          expanded[market] = true;
        });
        setExpandedMarkets(expanded);
      } else {
        // No hierarchy data - all locations are ungrouped
        setUngroupedLocations(uniqueLocations);
      }

      setSelectedLocations([]); // Start with none selected
    } catch (err) {
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLocations([...locations]);
    } else {
      setSelectedLocations([]);
    }
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations(prev => {
      if (prev.includes(location)) {
        return prev.filter(l => l !== location);
      } else {
        return [...prev, location];
      }
    });
  };

  // Handle market-level selection (select/deselect all stores in a market)
  const handleMarketToggle = (market) => {
    const marketStores = storesByMarket[market] || [];
    const allSelected = marketStores.every(store => selectedLocations.includes(store));

    if (allSelected) {
      // Deselect all stores in this market
      setSelectedLocations(prev => prev.filter(loc => !marketStores.includes(loc)));
    } else {
      // Select all stores in this market
      setSelectedLocations(prev => {
        const newSelection = [...prev];
        marketStores.forEach(store => {
          if (!newSelection.includes(store)) {
            newSelection.push(store);
          }
        });
        return newSelection;
      });
    }
  };

  // Check if all stores in a market are selected
  const isMarketFullySelected = (market) => {
    const marketStores = storesByMarket[market] || [];
    return marketStores.length > 0 && marketStores.every(store => selectedLocations.includes(store));
  };

  // Check if some (but not all) stores in a market are selected
  const isMarketPartiallySelected = (market) => {
    const marketStores = storesByMarket[market] || [];
    const selectedCount = marketStores.filter(store => selectedLocations.includes(store)).length;
    return selectedCount > 0 && selectedCount < marketStores.length;
  };

  // Toggle market accordion expansion
  const handleMarketExpand = (market) => (event, isExpanded) => {
    setExpandedMarkets(prev => ({
      ...prev,
      [market]: isExpanded
    }));
  };

  // Filter stores based on search term
  const getFilteredStores = (market) => {
    const stores = storesByMarket[market] || [];
    if (!searchTerm) return stores;
    return stores.filter(store => store.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  // Get filtered ungrouped locations
  const getFilteredUngrouped = () => {
    if (!searchTerm) return ungroupedLocations;
    return ungroupedLocations.filter(loc => loc.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  // Check if a market has any matching stores for search
  const marketHasMatchingStores = (market) => {
    if (!searchTerm) return true;
    const stores = storesByMarket[market] || [];
    return stores.some(store => store.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  const handleRunReport = async () => {
    // Resolve selections to store names based on report level
    let resolvedLocations = [];
    if (reportLevel === 'store') {
      resolvedLocations = selectedLocations;
    } else if (reportLevel === 'market') {
      selectedMarkets.forEach(market => {
        resolvedLocations.push(...(storesByMarket[market] || []));
      });
    } else if (reportLevel === 'channel') {
      selectedChannels.forEach(channel => {
        resolvedLocations.push(...(storesByChannel[channel] || []));
      });
    }
    resolvedLocations = [...new Set(resolvedLocations)];

    if (resolvedLocations.length === 0) {
      setError('Please select at least one location');
      return;
    }

    // For settlement reports, check if selected date is in the future
    if (reportType === 'settlement') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const reportDate = new Date(selectedDate);
      reportDate.setHours(0, 0, 0, 0);
      
      if (reportDate > today) {
        setError('Cannot generate report for future dates');
        return;
      }
    }

    try {
      setGenerating(true);
      setError(null);
      
      // Prepare parameters based on report type
      let params;
      if (reportType === 'settlement') {
        // For settlement report, send based on period type
        if (period === 'daily') {
          params = {
            locations: resolvedLocations,
            date: format(selectedDate, 'yyyy-MM-dd'),
            reportType: 'settlement',
            period: 'daily'
          };
        } else if (period === 'weekly') {
          params = {
            locations: resolvedLocations,
            date: format(selectedDate, 'yyyy-MM-dd'),
            reportType: 'settlement',
            period: 'weekly'
          };
        } else {
          params = {
            locations: resolvedLocations,
            month: selectedMonth,
            reportType: 'settlement',
            period: 'monthly'
          };
        }
      } else if (reportType === 'transactions') {
        // For transactions report, send based on period type
        if (period === 'daily') {
          params = {
            locations: resolvedLocations,
            date: format(selectedDate, 'yyyy-MM-dd'),
            reportType: 'transactions',
            period: 'daily'
          };
        } else if (period === 'weekly') {
          params = {
            locations: resolvedLocations,
            date: format(selectedDate, 'yyyy-MM-dd'),
            reportType: 'transactions',
            period: 'weekly'
          };
        } else {
          params = {
            locations: resolvedLocations,
            month: selectedMonth,
            reportType: 'transactions',
            period: 'monthly'
          };
        }
      } else if (reportType === 'surcharge') {
        params = period === 'monthly'
          ? { locations: resolvedLocations, month: selectedMonth, reportType: 'surcharge', period: 'monthly' }
          : { locations: resolvedLocations, date: format(selectedDate, 'yyyy-MM-dd'), reportType: 'surcharge', period };
      }
      
      // Call the actual API endpoint
      const response = await getSettlementReport(params);
      
      setReportData(response);
      
      // Expand all locations by default
      const expanded = {};
      selectedLocations.forEach(loc => {
        expanded[loc] = true;
      });
      setExpandedLocations(expanded);
    } catch (err) {
      // Check if no data is available for the selected date
      if (err.message && err.message.includes('CORS')) {
        setError(`${reportType === 'settlement' ? 'Settlement' : 'Transaction'} report feature is being configured. Please try again later.`);
      } else if (err.response?.status === 404) {
        setError(`No ${reportType === 'settlement' ? 'settlement' : 'transaction'} data available for the selected ${reportType === 'settlement' ? 'date' : 'month'} and locations.`);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(err.message || 'Failed to generate report. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Filter locations based on search term
  const filteredLocations = locations.filter(location =>
    location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAccordionToggle = (location) => (event, isExpanded) => {
    setExpandedLocations(prev => ({
      ...prev,
      [location]: isExpanded
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const formatAccountNumber = (account) => {
    if (!account) return null;
    const accountStr = account.toString();
    if (accountStr.length < 4) return account;
    return `x${accountStr.slice(-4)}`;
  };

  // Export Settlement Report to CSV
  const exportSettlementToCSV = () => {
    if (!reportData || !reportData.locations) {
      alert('No data available for export. Please generate the report first.');
      return;
    }

    const csvRows = [];

    // Filename + a Period value carried as a COLUMN (never a title row, so the file
    // parses cleanly as a CSV).
    let filename, periodLabel;
    if (period === 'daily') {
      periodLabel = format(selectedDate, 'yyyy-MM-dd');
      filename = `Settlement_Report_${format(selectedDate, 'yyyy-MM-dd')}.csv`;
    } else if (period === 'weekly') {
      const weekEnd = new Date(selectedDate);
      weekEnd.setDate(weekEnd.getDate() + 6);
      periodLabel = `${format(selectedDate, 'yyyy-MM-dd')} to ${format(weekEnd, 'yyyy-MM-dd')}`;
      filename = `Settlement_Report_Week_${format(selectedDate, 'yyyy-MM-dd')}.csv`;
    } else {
      periodLabel = selectedMonth;
      filename = `Settlement_Report_${selectedMonth}.csv`;
    }

    // SETTLEMENT data at the grain shown on screen: one row per LOCATION x TERMINAL
    // (transaction COUNT + settled amounts) — NOT individual card transactions, which are
    // what the separate Transactions export is for. Raw rows only: no title, no location
    // summary block, no daily/terminal sub-breakdowns, no subtotals, no grand total.
    csvRows.push('Location,Period,Terminal,Vault Account,Transactions,Settlement Amount,Withdrawal Amount,Surcharge');

    // Subtotal/grand-total rows are OPTIONAL, controlled by the "Include subtotals & totals
    // in export" checkbox (same flag the Transactions export uses). Unchecked = pure raw rows.
    let grandTxns = 0, grandSettlement = 0, grandWithdrawal = 0, grandSurcharge = 0;

    reportData.locations.forEach(location => {
      const settlementRows = location.transactions || [];
      let locTxns = 0, locSettlement = 0, locWithdrawal = 0, locSurcharge = 0;

      if (settlementRows.length > 0) {
        settlementRows.forEach(t => {
          const txns = parseInt(t.transactionCount || 0) || 0;
          const settlement = parseFloat(t.settlementAmount || 0);
          const withdrawal = parseFloat(t.withdrawalAmount || 0);
          const surcharge = parseFloat(t.surchargeAmount || 0);
          locTxns += txns;
          locSettlement += settlement;
          locWithdrawal += withdrawal;
          locSurcharge += surcharge;

          csvRows.push([
            `"${location.name || ''}"`,
            periodLabel,
            t.terminalId || '',
            t.vaultAccount ? `x${t.vaultAccount.toString().slice(-4)}` : '',
            txns,
            settlement.toFixed(2),
            withdrawal.toFixed(2),
            surcharge.toFixed(2)
          ].join(','));
        });

        // Per-location subtotal (only when the location actually had terminal rows —
        // otherwise it would just restate the single location-level row below).
        if (includeExportTotals) {
          csvRows.push([
            `"${location.name || ''} Subtotal"`,
            periodLabel,
            '',
            '',
            locTxns,
            locSettlement.toFixed(2),
            locWithdrawal.toFixed(2),
            locSurcharge.toFixed(2)
          ].join(','));
        }
      } else {
        // Location has settlement totals but no per-terminal rows (e.g. PAI/GH-sourced
        // locations). Emit ONE location-level row (blank Terminal) so no location silently
        // disappears from the export. Never emitted alongside terminal rows, so summing the
        // file can't double-count.
        locTxns = parseInt(location.totalTransactions || 0) || 0;
        locSettlement = parseFloat(location.totalAmount || 0);
        locWithdrawal = parseFloat(location.withdrawalAmount || 0);
        locSurcharge = parseFloat(location.surchargeAmount || 0);

        csvRows.push([
          `"${location.name || ''}"`,
          periodLabel,
          '',
          location.vaultAccount ? `x${location.vaultAccount.toString().slice(-4)}` : '',
          locTxns,
          locSettlement.toFixed(2),
          locWithdrawal.toFixed(2),
          locSurcharge.toFixed(2)
        ].join(','));
      }

      grandTxns += locTxns;
      grandSettlement += locSettlement;
      grandWithdrawal += locWithdrawal;
      grandSurcharge += locSurcharge;
    });

    if (includeExportTotals) {
      csvRows.push([
        '"Grand Total"',
        periodLabel,
        '',
        '',
        grandTxns,
        grandSettlement.toFixed(2),
        grandWithdrawal.toFixed(2),
        grandSurcharge.toFixed(2)
      ].join(','));
    }

    // Convert to CSV string
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Settlement Report to PDF
  const exportSettlementToPDF = () => {
    if (!reportData || !reportData.locations) {
      alert('No data available for export. Please generate the report first.');
      return;
    }

    const doc = new jsPDF();

    // Determine date range label and filename based on period
    let dateLabel, filename;
    if (period === 'daily') {
      dateLabel = format(selectedDate, 'MMMM d, yyyy');
      filename = `Settlement_Report_${format(selectedDate, 'yyyy-MM-dd')}.pdf`;
    } else if (period === 'weekly') {
      const weekEnd = new Date(selectedDate);
      weekEnd.setDate(weekEnd.getDate() + 6);
      dateLabel = `${format(selectedDate, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      filename = `Settlement_Report_Week_${format(selectedDate, 'yyyy-MM-dd')}.pdf`;
    } else {
      dateLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;
      filename = `Settlement_Report_${selectedMonth}.pdf`;
    }

    // Title
    doc.setFontSize(16);
    doc.text('Settlement Report', 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.text(dateLabel, 105, 28, { align: 'center' });

    // Summary section
    doc.setFontSize(14);
    doc.text('Summary', 14, 40);
    doc.setFontSize(10);

    const summaryData = [
      ['Total Locations', (reportData.summary?.totalLocations || 0).toString()],
      ['Total Terminals', formatNumber(reportData.summary?.totalTerminals || 0)],
      ['Total Transactions', formatNumber(reportData.summary?.totalTransactions || 0)],
      ['Total Withdrawals', formatCurrency(reportData.summary?.totalWithdrawals || 0)],
      ['Total Settlement', formatCurrency(reportData.summary?.totalAmount || 0)],
      ['Total Surcharge', formatCurrency(reportData.summary?.totalSurcharge || 0)]
    ];

    doc.autoTable({
      startY: 45,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [66, 165, 245] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 60, halign: 'right' }
      }
    });

    // Location Details
    doc.setFontSize(14);
    doc.text('Location Details', 14, doc.lastAutoTable.finalY + 15);

    const locationData = reportData.locations.map(location => [
      location.name || '',
      location.vaultAccount ? 'x' + location.vaultAccount.toString().slice(-4) : '',
      (location.terminals?.length || 0).toString(),
      formatNumber(location.totalTransactions || 0),
      formatCurrency(location.totalAmount || 0),
      formatCurrency(location.withdrawalAmount || 0),
      formatCurrency(location.surchargeAmount || 0)
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Location', 'Vault', 'Terminals', 'Transactions', 'Settlement', 'Withdrawals', 'Surcharge']],
      body: locationData,
      theme: 'striped',
      headStyles: { fillColor: [66, 165, 245] },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 20 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' },
        6: { cellWidth: 25, halign: 'right' }
      }
    });

    // Add daily breakdown for weekly/monthly reports
    if (period === 'weekly' || period === 'monthly') {
      reportData.locations.forEach(location => {
        if (location.dailyData?.length > 0) {
          // Check if we need a new page
          if (doc.lastAutoTable.finalY > 240) {
            doc.addPage();
            doc.setFontSize(14);
            doc.text(`Daily Breakdown - ${location.name}`, 14, 20);
          } else {
            doc.setFontSize(12);
            doc.text(`Daily Breakdown - ${location.name}`, 14, doc.lastAutoTable.finalY + 15);
          }

          const dailyData = location.dailyData.map(day => [
            day.date,
            formatNumber(day.transactionCount || 0),
            formatCurrency(day.settlementAmount || 0),
            formatCurrency(day.withdrawalAmount || 0),
            formatCurrency(day.surchargeAmount || 0)
          ]);

          // Add totals row
          dailyData.push([
            'Total',
            formatNumber(location.totalTransactions || 0),
            formatCurrency(location.totalAmount || 0),
            formatCurrency(location.withdrawalAmount || 0),
            formatCurrency(location.surchargeAmount || 0)
          ]);

          doc.autoTable({
            startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 30,
            head: [['Date', 'Transactions', 'Settlement', 'Withdrawals', 'Surcharge']],
            body: dailyData,
            theme: 'striped',
            headStyles: { fillColor: [100, 181, 246] },
            styles: { fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 35 },
              1: { cellWidth: 30, halign: 'right' },
              2: { cellWidth: 35, halign: 'right' },
              3: { cellWidth: 35, halign: 'right' },
              4: { cellWidth: 35, halign: 'right' }
            },
            didParseCell: function(data) {
              // Bold the totals row
              if (data.row.index === dailyData.length - 1) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [240, 240, 240];
              }
            }
          });
        }
      });
    }

    // Save the PDF
    doc.save(filename);
  };

  const handleExportTransactions = async (location) => {
    try {
      console.log('Starting export for location:', location);

      // Determine period label for filename
      let periodLabel;
      if (period === 'daily') {
        periodLabel = format(selectedDate, 'yyyy-MM-dd');
      } else if (period === 'weekly') {
        periodLabel = `Week_${format(selectedDate, 'yyyy-MM-dd')}`;
      } else {
        periodLabel = selectedMonth;
      }

      // If we already have individual transactions loaded, export from that
      if (reportData?.reportType === 'individual_transactions' && location.transactions?.length > 0) {
        console.log('Exporting from already-loaded individual transactions');

        const csvRows = [];
        csvRows.push('Location,Date,Time,Terminal,Card,Dispensed,Surcharge,Total,Type,Reversed,RRN');

        location.transactions.forEach(txn => {
          csvRows.push([
            `"${location.name}"`,
            txn.transactionDate,
            txn.transactionTime,
            txn.terminalId,
            `****${txn.cardLast4 || ''}`,
            txn.dispensedAmount || 0,
            txn.surchargeAmount || 0,
            txn.settlementAmount || 0,
            txn.transactionType || 'WTH',
            txn.isReversed ? 'Yes' : 'No',
            txn.rrn || ''
          ].join(','));
        });

        // Add totals row + net summary (export-only; toggled by "Include subtotals & totals")
        if (includeExportTotals) {
          csvRows.push('');
          csvRows.push([
            '"TOTALS"',
            '',
            '',
            '',
            '',
            location.totalDispensed || 0,
            location.totalSurcharge || 0,
            location.totalAmount || 0,
            `${location.transactions.length} transactions`
          ].join(','));
          // Reversal netting summary (Net figures reconcile to settlement)
          csvRows.push('');
          csvRows.push(`Reversals,${location.reversalCount || 0} txns,${location.reversalAmount || 0}`);
          csvRows.push(`Net Dispensed,,${location.netDispensed ?? location.totalDispensed ?? 0}`);
          csvRows.push(`Net Surcharge,,${location.netSurcharge ?? location.totalSurcharge ?? 0}`);
          csvRows.push(`Net Total,,${location.netAmount ?? location.totalAmount ?? 0}`);
        }

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${location.name.replace(/\s+/g, '_')}_transactions_${periodLabel}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        console.log('Export completed successfully from loaded data');
        return;
      }

      // Fallback: Validate inputs for API call
      if (!location || !location.hgTerminals || !selectedMonth) {
        console.error('Missing required data for export:', { location, selectedMonth });
        setError('Missing required data for export. Please try again.');
        return;
      }
      
      // Call API to get detailed transactions for HG terminals
      console.log('Calling API with:', {
        reportType: 'transactions',
        location: location.name,
        hgTerminals: location.hgTerminals,
        month: selectedMonth
      });
      
      // Use download-report endpoint as workaround for resource limit
      const response = await apiCall('/api/v1/pai/download-report', 'POST', {
        reportType: 'transactions',
        location: location.name,
        hgTerminals: location.hgTerminals,
        month: selectedMonth
      });
      
      console.log('API response received:', response);
      
      // Check if we have transactions
      if (!response || !response.transactions || !Array.isArray(response.transactions)) {
        console.error('Invalid response format:', response);
        setError('No transactions found for export.');
        return;
      }
      
      // Create CSV content
      const headers = ['Location', 'Date', 'Time', 'Terminal', 'Card Last 4', 'Amount', 'Surcharge', 'Total', 'Response Code', 'Response Description'];
      const rows = response.transactions.map(tx => {
        try {
          // Safely parse the date and time
          let dateStr = '';
          let timeStr = '';

          if (tx.trx_date_time) {
            // Try to parse as Date object
            const txDate = new Date(tx.trx_date_time);
            if (txDate instanceof Date && !isNaN(txDate) && typeof format === 'function') {
              dateStr = format(txDate, 'MM/dd/yyyy');
              timeStr = format(txDate, 'HH:mm:ss');
            } else {
              // Fallback to string manipulation
              const parts = tx.trx_date_time.split(' ');
              dateStr = parts[0] || '';
              timeStr = parts[1] || '';
            }
          }

          return [
            tx.location || location.name || '',
            dateStr,
            timeStr,
            tx.terminal || '',
            tx.card_number || '',
            tx.req_amt || 0,
            tx.surch_amt || 0,
            (parseFloat(tx.req_amt || 0) + parseFloat(tx.surch_amt || 0)).toFixed(2),
            tx.response_code || '',
            tx.response_description || ''
          ];
        } catch (rowError) {
          console.error('Error processing transaction row:', tx, rowError);
          return [tx.location || location.name || '', '', '', tx.terminal || '', '', '0', '0', '0', '', ''];
        }
      });
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Download the file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${location.name.replace(/\s+/g, '_')}_transactions_${selectedMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('Export completed successfully');
    } catch (error) {
      console.error('Export failed:', error);
      console.error('Error stack:', error.stack);
      setError('Failed to export transactions. Please try again.');
    }
  };

  const handleExportAllTransactions = async () => {
    try {
      console.log('Starting export for all locations');

      if (!reportData.locations || reportData.locations.length === 0) {
        setError('No locations found to export.');
        return;
      }

      // Determine period label for filename
      let periodLabel;
      if (period === 'daily') {
        periodLabel = format(selectedDate, 'yyyy-MM-dd');
      } else if (period === 'weekly') {
        periodLabel = `Week_${format(selectedDate, 'yyyy-MM-dd')}`;
      } else {
        periodLabel = selectedMonth;
      }

      // Monthly reports are summary-only on screen (by design — a raw month at channel scale
      // blows the API caps), so for the EXPORT fetch per-transaction detail one location per
      // request via detailExport (the backend routes a single location through the same detail
      // machinery the weekly report uses).
      let exportLocations = reportData.locations;
      let exportAsIndividual = reportData.reportType === 'individual_transactions';
      if (reportData.reportType === 'monthly_summary') {
        const detailed = [];
        for (const loc of reportData.locations) {
          if (!loc.totalTransactions && !loc.reversalCount) continue;
          const resp = await getSettlementReport({
            locations: [loc.name],
            month: selectedMonth,
            reportType: 'transactions',
            period: 'monthly',
            detailExport: true
          });
          (resp?.locations || []).forEach(l => detailed.push(l));
        }
        if (detailed.some(l => l.transactions && l.transactions.length > 0)) {
          exportLocations = detailed;
          exportAsIndividual = true;
        }
      }

      // Create CSV content
      const csvRows = [];

      // Check if we have individual transactions
      if (exportAsIndividual) {
        // Header for individual transactions
        csvRows.push('Location,Date,Time,Terminal,Card,Requested,Dispensed,Surcharge,Total,Type,Reversed,Response,Seq #,RRN');

        // Data rows - flatten individual transactions from all locations
        exportLocations.forEach(location => {
          if (location.transactions && location.transactions.length > 0) {
            location.transactions.forEach(txn => {
              csvRows.push([
                `"${location.name}"`,
                txn.transactionDate,
                txn.transactionTime,
                txn.terminalId,
                `****${txn.cardLast4 || ''}`,
                txn.requestedAmount || txn.dispensedAmount || 0,
                txn.dispensedAmount || 0,
                txn.surchargeAmount || 0,
                txn.settlementAmount || 0,
                txn.transactionType || 'WTH',
                txn.isReversed ? 'Yes' : 'No',
                txn.responseDescription || (txn.responseCode === 0 ? 'Approved' : 'Declined'),
                txn.sequenceNumber || '',
                txn.rrn || ''
              ].join(','));
            });

            // Subtotal for location (export-only; toggled by "Include subtotals & totals")
            if (includeExportTotals) {
              csvRows.push([
                `"${location.name} SUBTOTAL"`,
                '',
                '',
                '',
                '',
                location.totalDispensed || 0,
                location.totalSurcharge || 0,
                location.totalAmount || 0,
                `${location.transactions.length} transactions`
              ].join(','));
              csvRows.push('');
            }
          }
        });
      } else {
        // Header for summary data
        csvRows.push('Location,Date,Transactions,Dispensed,Surcharge,Total');

        // Data rows - flatten daily data from all locations
        reportData.locations.forEach(location => {
          if (location.dailyData && location.dailyData.length > 0) {
            location.dailyData.forEach(day => {
              csvRows.push([
                `"${location.name}"`,
                day.date,
                day.transactions || 0,
                day.dispensed || 0,
                day.surcharge || 0,
                day.total || 0
              ].join(','));
            });
          } else {
            // If no daily data, add summary row
            csvRows.push([
              `"${location.name}"`,
              periodLabel,
              location.totalTransactions || 0,
              location.totalDispensed || 0,
              location.totalSurcharge || 0,
              location.totalAmount || 0
            ].join(','));
          }
        });
      }

      // Add totals row + net summary (export-only; toggled by "Include subtotals & totals")
      if (includeExportTotals) {
        csvRows.push('');
        csvRows.push([
          '"TOTALS"',
          '',
          reportData.summary?.totalTransactions || 0,
          reportData.summary?.totalDispensed || 0,
          reportData.summary?.totalSurcharge || 0,
          reportData.summary?.totalAmount || 0
        ].join(','));
        // Reversal netting summary (Net figures reconcile to settlement)
        csvRows.push('');
        csvRows.push(`Reversals,${reportData.summary?.totalReversalCount || 0} txns,${reportData.summary?.totalReversals || 0}`);
        csvRows.push(`Net Dispensed,,${reportData.summary?.totalNetDispensed ?? reportData.summary?.totalDispensed ?? 0}`);
        csvRows.push(`Net Surcharge,,${reportData.summary?.totalNetSurcharge ?? reportData.summary?.totalSurcharge ?? 0}`);
        csvRows.push(`Net Total,,${reportData.summary?.totalNetAmount ?? reportData.summary?.totalAmount ?? 0}`);
      }

      const csvContent = csvRows.join('\n');

      // Download the file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `All_Locations_Transactions_${periodLabel}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log(`Export completed: ${reportData.locations.length} locations`);
    } catch (error) {
      console.error('Export all failed:', error);
      setError('Failed to export all transactions. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Settlement Reports
        </Typography>
        
        <Grid container spacing={3}>
          {/* Left Panel - Location Selection and Date */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                {/* Report Type Dropdown */}
                <Typography variant="h6" gutterBottom>
                  <AssessmentIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                  Report Type
                </Typography>
                <Box component="select"
                  value={reportType}
                  onChange={(e) => handleReportTypeChange(e.target.value)}
                  sx={nativeSelectSx}
                >
                  <option value="settlement">Settlement</option>
                  <option value="transactions">Transactions</option>
                  {isAdmin && <option value="surcharge">Surcharge (Platform B)</option>}
                </Box>
                
                <Divider sx={{ my: 2 }} />

                {/* Report Period - single persistent Select (not conditionally rendered) */}
                <Typography variant="h6" gutterBottom>
                  <CalendarIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                  Report Period
                </Typography>
                <Box component="select"
                  value={period}
                  onChange={(e) => {
                    setPeriod(e.target.value);
                    setReportData(null);
                  }}
                  sx={nativeSelectSx}
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                </Box>
                <Divider sx={{ my: 2 }} />

                {/* Date Selection - Show different selector based on period */}
                <Typography variant="h6" gutterBottom>
                  <CalendarIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                  {period === 'daily' ? 'Report Date' :
                   period === 'weekly' ? 'Week Starting' : 'Report Month'}
                </Typography>

                {period === 'daily' || period === 'weekly' ? (
                  <DatePicker
                    value={selectedDate}
                    onChange={(newDate) => {
                      setSelectedDate(newDate);
                      setReportData(null); // Clear report when date changes
                    }}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        helperText: period === 'weekly'
                          ? 'Select the starting date of the week'
                          : reportType === 'settlement'
                          ? 'Settlement reports are typically for previous day'
                          : 'Select date for transaction report'
                      }
                    }}
                  />
                ) : (
                  <Box component="select"
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setReportData(null);
                    }}
                    sx={nativeSelectSx}
                  >
                    {monthOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />

                {/* Report Level Toggle */}
                <Typography variant="h6" gutterBottom>
                  <FilterListIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                  Report Level
                </Typography>
                <ToggleButtonGroup
                  value={reportLevel}
                  exclusive
                  onChange={(e, val) => {
                    if (val) {
                      setReportLevel(val);
                      setSelectedLocations([]);
                      setSelectedMarkets([]);
                      setSelectedChannels([]);
                      setReportData(null);
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {channels.length > 0 && <ToggleButton value="channel">Channel</ToggleButton>}
                  {markets.length > 0 && <ToggleButton value="market">Market</ToggleButton>}
                  <ToggleButton value="store">Store</ToggleButton>
                </ToggleButtonGroup>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  <LocationIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                  {reportLevel === 'channel' ? 'Select Channels' : reportLevel === 'market' ? 'Select Markets' : 'Select Locations'}
                </Typography>

                {/* Selected Items Chips */}
                {reportLevel === 'channel' && selectedChannels.length > 0 && (
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedChannels.map(channel => (
                      <Chip
                        key={channel}
                        label={`${channel} (${(storesByChannel[channel] || []).length} stores)`}
                        size="small"
                        onDelete={() => setSelectedChannels(prev => prev.filter(c => c !== channel))}
                        color="primary"
                      />
                    ))}
                    {selectedChannels.length > 1 && (
                      <Chip label="Clear All" size="small" onClick={() => setSelectedChannels([])} variant="outlined" color="error" sx={{ cursor: 'pointer' }} />
                    )}
                  </Box>
                )}
                {reportLevel === 'market' && selectedMarkets.length > 0 && (
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedMarkets.map(market => (
                      <Chip
                        key={market}
                        label={`${market} (${(storesByMarket[market] || []).length} stores)`}
                        size="small"
                        onDelete={() => setSelectedMarkets(prev => prev.filter(m => m !== market))}
                        color="primary"
                      />
                    ))}
                    {selectedMarkets.length > 1 && (
                      <Chip label="Clear All" size="small" onClick={() => setSelectedMarkets([])} variant="outlined" color="error" sx={{ cursor: 'pointer' }} />
                    )}
                  </Box>
                )}
                {reportLevel === 'store' && selectedLocations.length > 0 && (
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedLocations.map(location => (
                      <Chip
                        key={location}
                        label={location}
                        size="small"
                        onDelete={() => handleLocationToggle(location)}
                        color="primary"
                        sx={{ maxWidth: '200px' }}
                      />
                    ))}
                    {selectedLocations.length > 1 && (
                      <Chip
                        label="Clear All"
                        size="small"
                        onClick={() => setSelectedLocations([])}
                        variant="outlined"
                        color="error"
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Box>
                )}
                
                {/* Channel Picker */}
                {reportLevel === 'channel' && (
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedChannels.length === channels.length && channels.length > 0}
                            indeterminate={selectedChannels.length > 0 && selectedChannels.length < channels.length}
                            onChange={(e) => setSelectedChannels(e.target.checked ? [...channels] : [])}
                          />
                        }
                        label={<strong>Select All</strong>}
                        sx={{ mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}
                      />
                      {channels.map(channel => (
                        <FormControlLabel
                          key={channel}
                          control={
                            <Checkbox
                              checked={selectedChannels.includes(channel)}
                              onChange={() => setSelectedChannels(prev =>
                                prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                              <Typography variant="body2">{channel}</Typography>
                              <Chip label={`${(storesByChannel[channel] || []).length} stores`} size="small" variant="outlined" sx={{ ml: 1 }} />
                            </Box>
                          }
                          sx={{
                            mb: 0.5, py: 0.5, px: 1, borderRadius: 1,
                            border: '1px solid',
                            borderColor: selectedChannels.includes(channel) ? 'primary.main' : 'divider',
                            backgroundColor: selectedChannels.includes(channel) ? 'primary.lighter' : 'transparent'
                          }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}

                {/* Market Picker */}
                {reportLevel === 'market' && (
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedMarkets.length === markets.length && markets.length > 0}
                            indeterminate={selectedMarkets.length > 0 && selectedMarkets.length < markets.length}
                            onChange={(e) => setSelectedMarkets(e.target.checked ? [...markets] : [])}
                          />
                        }
                        label={<strong>Select All</strong>}
                        sx={{ mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}
                      />
                      {markets.map(market => (
                        <FormControlLabel
                          key={market}
                          control={
                            <Checkbox
                              checked={selectedMarkets.includes(market)}
                              onChange={() => setSelectedMarkets(prev =>
                                prev.includes(market) ? prev.filter(m => m !== market) : [...prev, market]
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                              <Typography variant="body2">{market}</Typography>
                              <Chip label={`${(storesByMarket[market] || []).length}`} size="small" variant="outlined" sx={{ ml: 1 }} />
                            </Box>
                          }
                          sx={{
                            mb: 0.5, py: 0.5, px: 1, borderRadius: 1,
                            border: '1px solid',
                            borderColor: selectedMarkets.includes(market) ? 'primary.main' : 'divider',
                            backgroundColor: selectedMarkets.includes(market) ? 'primary.lighter' : 'transparent'
                          }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}

                {/* Store Picker (existing behavior) */}
                {reportLevel === 'store' && (<>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {markets.length > 0 ? `${markets.length} markets` : `${locations.length} locations`}
                  </Typography>
                  {selectedLocations.length > 0 && (
                    <Typography variant="body2" color="primary">
                      {selectedLocations.length} selected
                    </Typography>
                  )}
                </Box>

                {/* Select All */}
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedLocations.length === locations.length && locations.length > 0}
                        indeterminate={selectedLocations.length > 0 && selectedLocations.length < locations.length}
                        onChange={handleSelectAll}
                      />
                    }
                    label={<strong>Select All</strong>}
                    sx={{ mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}
                  />
                </FormGroup>

                {/* Market Accordions */}
                <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {markets.filter(market => marketHasMatchingStores(market)).map(market => (
                    <Accordion
                      key={market}
                      expanded={expandedMarkets[market] || false}
                      onChange={handleMarketExpand(market)}
                      sx={{
                        mb: 0.5,
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: isMarketFullySelected(market) ? 'primary.main' : 'divider',
                        backgroundColor: isMarketFullySelected(market) ? 'primary.lighter' : 'transparent'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 40,
                          '& .MuiAccordionSummary-content': { margin: '8px 0' },
                          backgroundColor: isMarketFullySelected(market) ? 'primary.light' : 'grey.100'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Checkbox
                            checked={isMarketFullySelected(market)}
                            indeterminate={isMarketPartiallySelected(market)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleMarketToggle(market);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: isMarketFullySelected(market) ? 'bold' : 'medium',
                              color: isMarketFullySelected(market) ? 'primary.contrastText' : 'text.primary'
                            }}
                          >
                            {market}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 1,
                              color: isMarketFullySelected(market) ? 'primary.contrastText' : 'text.secondary'
                            }}
                          >
                            ({(storesByMarket[market] || []).length} stores)
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <FormGroup>
                          {getFilteredStores(market).map(store => (
                            <FormControlLabel
                              key={store}
                              control={
                                <Checkbox
                                  checked={selectedLocations.includes(store)}
                                  onChange={() => handleLocationToggle(store)}
                                  size="small"
                                />
                              }
                              label={store}
                              sx={{
                                display: 'flex',
                                width: '100%',
                                ml: 2,
                                mr: 0,
                                backgroundColor: selectedLocations.includes(store) ? 'action.hover' : 'transparent',
                                borderRadius: 1,
                                mb: 0,
                                '& .MuiFormControlLabel-label': {
                                  width: '100%',
                                  fontSize: '0.875rem',
                                  fontWeight: selectedLocations.includes(store) ? 'medium' : 'normal'
                                }
                              }}
                            />
                          ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  ))}

                  {/* Ungrouped Locations (if any) */}
                  {getFilteredUngrouped().length > 0 && (
                    <Accordion
                      expanded={expandedMarkets['_ungrouped'] || false}
                      onChange={handleMarketExpand('_ungrouped')}
                      sx={{
                        mb: 0.5,
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 40,
                          '& .MuiAccordionSummary-content': { margin: '8px 0' },
                          backgroundColor: 'grey.50'
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          Other Locations ({getFilteredUngrouped().length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <FormGroup>
                          {getFilteredUngrouped().map(location => (
                            <FormControlLabel
                              key={location}
                              control={
                                <Checkbox
                                  checked={selectedLocations.includes(location)}
                                  onChange={() => handleLocationToggle(location)}
                                  size="small"
                                />
                              }
                              label={location}
                              sx={{
                                display: 'flex',
                                width: '100%',
                                ml: 2,
                                mr: 0,
                                backgroundColor: selectedLocations.includes(location) ? 'action.hover' : 'transparent',
                                borderRadius: 1,
                                mb: 0,
                                '& .MuiFormControlLabel-label': {
                                  width: '100%',
                                  fontSize: '0.875rem',
                                  fontWeight: selectedLocations.includes(location) ? 'medium' : 'normal'
                                }
                              }}
                            />
                          ))}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
                </>)}

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleRunReport}
                  disabled={generating || (reportLevel === 'store' ? selectedLocations.length === 0 : reportLevel === 'market' ? selectedMarkets.length === 0 : selectedChannels.length === 0)}
                  startIcon={generating ? <CircularProgress size={20} /> : <AssessmentIcon />}
                >
                  {generating ? 'Generating Report...' : 'Run Report'}
                </Button>

                {(selectedLocations.length > 0 || selectedMarkets.length > 0 || selectedChannels.length > 0) && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                    {reportLevel === 'store'
                      ? `${selectedLocations.length} location${selectedLocations.length !== 1 ? 's' : ''} selected`
                      : reportLevel === 'market'
                      ? `${selectedMarkets.length} market${selectedMarkets.length !== 1 ? 's' : ''} (${selectedMarkets.reduce((sum, m) => sum + (storesByMarket[m] || []).length, 0)} stores)`
                      : `${selectedChannels.length} channel${selectedChannels.length !== 1 ? 's' : ''} (${selectedChannels.reduce((sum, c) => sum + (storesByChannel[c] || []).length, 0)} stores)`
                    }
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Right Panel - Report Results */}
          <Grid item xs={12} md={9}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {!reportData && !generating && (
              <Card>
                <CardContent>
                  <Box textAlign="center" py={5}>
                    <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Select locations and a date, then click "Run Report" to generate a settlement report
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {reportData && (
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                      {reportType === 'settlement'
                        ? period === 'daily'
                          ? `Settlement Report for ${format(selectedDate, 'MMMM d, yyyy')}`
                          : period === 'weekly'
                          ? `Settlement Report for Week of ${format(selectedDate, 'MMM d, yyyy')}`
                          : `Settlement Report for ${monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth}`
                        : reportType === 'surcharge'
                        ? period === 'daily'
                          ? `Surcharge Report for ${format(selectedDate, 'MMMM d, yyyy')}`
                          : period === 'weekly'
                          ? `Surcharge Report for Week of ${format(selectedDate, 'MMM d, yyyy')}`
                          : `Surcharge Report for ${monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth}`
                        : period === 'daily'
                          ? `Transactions Report for ${format(selectedDate, 'MMMM d, yyyy')}`
                          : period === 'weekly'
                          ? `Transactions Report for Week of ${format(selectedDate, 'MMM d, yyyy')}`
                          : `Transactions Report for ${monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth}`
                      }
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setReportData(null)}
                      size="small"
                    >
                      Clear Report
                    </Button>
                  </Box>

                  {reportType === 'surcharge' ? (
                    <SurchargeReportBody data={reportData} />
                  ) : (
                  <>
                  {/* Summary Statistics */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={2.4}>
                      <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Locations
                        </Typography>
                        <Typography variant="h6">
                          {reportData.summary?.totalLocations || 0}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Terminals
                        </Typography>
                        <Typography variant="h6">
                          {formatNumber(reportData.summary?.totalTerminals || 0)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Transactions
                        </Typography>
                        <Typography variant="h6">
                          {formatNumber(reportData.summary?.totalTransactions || 0)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {reportType === 'settlement' ? 'Total Withdrawals' : 'Total Dispensed'}
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(reportType === 'settlement' 
                            ? reportData.summary?.totalWithdrawals || 0
                            : reportData.summary?.totalDispensed || 0)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {reportType === 'settlement' ? 'Total Settlement' : 'Total Amount'}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(reportData.summary?.totalAmount || 0)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={2.4}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Surcharge
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(reportData.summary?.totalSurcharge || 0)}
                        </Typography>
                      </Paper>
                    </Grid>
                    {/* Reversal + Net cards (transactions report) — Net figures net out reversed
                        withdrawals and reconcile to settlement */}
                    {reportType === 'transactions' && (
                      <>
                        <Grid item xs={6} sm={2.4}>
                          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#fff3e0' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Reversals
                            </Typography>
                            <Typography variant="h6" color="warning.main" fontWeight="bold">
                              {formatNumber(reportData.summary?.totalReversalCount || 0)} · {formatCurrency(reportData.summary?.totalReversals || 0)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Paper elevation={3} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9', borderColor: 'success.main', borderWidth: 2, borderStyle: 'solid' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Net Dispensed
                            </Typography>
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              {formatCurrency(reportData.summary?.totalNetDispensed ?? reportData.summary?.totalDispensed ?? 0)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Paper elevation={3} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9', borderColor: 'success.main', borderWidth: 2, borderStyle: 'solid' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Net Surcharge
                            </Typography>
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              {formatCurrency(reportData.summary?.totalNetSurcharge ?? reportData.summary?.totalSurcharge ?? 0)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Net Total
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {formatCurrency(reportData.summary?.totalNetAmount ?? reportData.summary?.totalAmount ?? 0)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  
                  {/* Export All button for settlement reports */}
                  {reportType === 'settlement' && reportData.locations && reportData.locations.length > 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={exportSettlementToCSV}
                        startIcon={<GetAppIcon />}
                        sx={{ mr: 2 }}
                      >
                        Export All Settlement
                      </Button>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeExportTotals}
                            onChange={(e) => setIncludeExportTotals(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Include subtotals & totals in export"
                      />
                    </Box>
                  )}

                  {/* Export All button for transactions reports */}
                  {reportType === 'transactions' && reportData.locations && reportData.locations.length > 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleExportAllTransactions()}
                        startIcon={<GetAppIcon />}
                        sx={{ mr: 2 }}
                      >
                        Export All Transactions
                      </Button>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeExportTotals}
                            onChange={(e) => setIncludeExportTotals(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Include subtotals & totals in export"
                      />
                    </Box>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Location Details with Collapsible Transactions */}
                  <Typography variant="h6" gutterBottom>
                    Location Details
                  </Typography>
                  
                  {reportData.locations && reportData.locations.map(location => (
                    <Accordion
                      key={location.name}
                      expanded={expandedLocations[location.name] || false}
                      onChange={handleAccordionToggle(location.name)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Grid container alignItems="center" spacing={2} sx={{ width: '100%' }}>
                          <Grid item xs={12} md={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LocationIcon color="primary" />
                              <Box>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {location.name}
                                </Typography>
                                {location.vaultAccount && (
                                  <Typography variant="caption" color="success.main" fontWeight="medium">
                                    Vault: {formatAccountNumber(location.vaultAccount)}
                                  </Typography>
                                )}
                              </Box>
                              <Chip 
                                label={`${location.terminals?.length || 0} terminals`} 
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Typography variant="body2" color="text.secondary">
                              Transactions
                            </Typography>
                            <Typography variant="body1">
                              {formatNumber(location.totalTransactions || 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Typography variant="body2" color="text.secondary">
                              {reportType === 'settlement' ? 'Settlement' : 'Total Amount'}
                            </Typography>
                            <Typography variant="body1" color="primary">
                              {formatCurrency(location.totalAmount || 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Typography variant="body2" color="text.secondary">
                              {reportType === 'settlement' ? 'Withdrawals' : 'Dispensed'}
                            </Typography>
                            <Typography variant="body1">
                              {formatCurrency(reportType === 'settlement' 
                                ? location.withdrawalAmount || 0
                                : location.totalDispensed || 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Typography variant="body2" color="text.secondary">
                              Total Surcharge
                            </Typography>
                            <Typography variant="body1" color="success.main">
                              {formatCurrency(reportType === 'settlement'
                                ? location.surchargeAmount || 0
                                : location.totalSurcharge || 0)}
                            </Typography>
                          </Grid>
                          {reportType === 'transactions' && location.reversalCount > 0 && (
                            <>
                              <Grid item xs={6} md={2}>
                                <Typography variant="body2" color="text.secondary">
                                  Reversals
                                </Typography>
                                <Typography variant="body1" color="warning.main">
                                  {formatNumber(location.reversalCount || 0)} · {formatCurrency(location.reversalAmount || 0)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={2}>
                                <Typography variant="body2" color="text.secondary">
                                  Net Dispensed
                                </Typography>
                                <Typography variant="body1" color="success.main">
                                  {formatCurrency(location.netDispensed ?? location.totalDispensed ?? 0)}
                                </Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Show different content based on report type */}
                        {reportType === 'transactions' ? (
                          // Transactions Report
                          <Box sx={{ p: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Box>
                                {reportData.reportType === 'monthly_summary' ? (
                                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                    Monthly Summary — {formatNumber(location.totalTransactions || 0)} transactions
                                    {(location.reversalCount || 0) > 0 && ` • ${formatNumber(location.reversalCount)} reversals (${formatCurrency(location.reversalAmount || 0)})`}
                                  </Typography>
                                ) : reportData.reportType === 'individual_transactions' ? (
                                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                    Individual Transactions ({location.transactions?.length || 0})
                                  </Typography>
                                ) : (
                                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                    Daily Transaction Breakdown for {monthOptions.find(m => m.value === selectedMonth)?.label}
                                  </Typography>
                                )}
                                {/* Show terminal mappings only when there ARE mappings — isPAI is
                                    hacked true on non-PAI arms to enable the transactions view,
                                    which used to render these labels empty (e.g. Rise Pasadena). */}
                                {(location.hgTerminals?.length > 0 || location.ghTerminals?.length > 0) && (
                                  <Box sx={{ mt: 1 }}>
                                    {location.hgTerminals?.length > 0 && (
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        <strong>Settled under:</strong> {location.hgTerminals.join(', ')}
                                      </Typography>
                                    )}
                                    {location.ghTerminals?.length > 0 && (
                                      <Typography variant="body2" color="text.secondary">
                                        <strong>Maps to GH Terminals:</strong> {location.ghTerminals.join(', ')}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                              {/* Per-location export removed 2026-08-17 (Todd) — the green
                                  "Export All Transactions" button above covers exports. */}
                            </Box>

                            {/* Individual transactions table - PAI format */}
                            {reportData.reportType === 'individual_transactions' && location.transactions?.length > 0 ? (
                              <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 500 }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                      <TableCell><strong>Date/Time</strong></TableCell>
                                      <TableCell><strong>Terminal</strong></TableCell>
                                      <TableCell><strong>Card Number</strong></TableCell>
                                      <TableCell align="right"><strong>Requested</strong></TableCell>
                                      <TableCell align="right"><strong>Dispensed</strong></TableCell>
                                      <TableCell align="right"><strong>Surcharge</strong></TableCell>
                                      <TableCell align="right"><strong>Total</strong></TableCell>
                                      <TableCell><strong>Type</strong></TableCell>
                                      <TableCell><strong>Response</strong></TableCell>
                                      <TableCell><strong>Seq #</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {location.transactions.map((txn, idx) => (
                                      <TableRow key={`${txn.rrn}-${idx}`} hover sx={txn.isReversed ? { backgroundColor: '#fff8e1' } : undefined}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                          {txn.transactionDate} {txn.transactionTime}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={txn.terminalId} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>****{txn.cardLast4}</TableCell>
                                        <TableCell align="right">{formatCurrency(txn.requestedAmount)}</TableCell>
                                        <TableCell align="right">{formatCurrency(txn.dispensedAmount)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>
                                          {formatCurrency(txn.surchargeAmount)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                          {formatCurrency(txn.settlementAmount)}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={txn.transactionType || 'WTH'}
                                            size="small"
                                            color={transactionTypeColor(txn.transactionType || 'WTH')}
                                          />
                                          {txn.isReversed && (
                                            <Chip label="Reversed" size="small" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                          {txn.responseDescription || 'Approved'}
                                        </TableCell>
                                        <TableCell>{txn.sequenceNumber}</TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                                        Total ({location.transactions.length} transactions)
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(location.transactions.reduce((sum, t) => sum + (t.requestedAmount || 0), 0))}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(location.totalDispensed)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                        {formatCurrency(location.totalSurcharge)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(location.totalAmount)}
                                      </TableCell>
                                      <TableCell colSpan={3}></TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              /* Daily summary table (fallback) */
                              <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Date</TableCell>
                                      <TableCell align="right">Transactions</TableCell>
                                      <TableCell align="right">Dispensed</TableCell>
                                      <TableCell align="right">Surcharge</TableCell>
                                      <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {location.dailyData?.map((day) => (
                                      <TableRow key={day.date}>
                                        <TableCell>{format(new Date(day.date + 'T12:00:00'), 'MMM dd, yyyy')}</TableCell>
                                        <TableCell align="right">{formatNumber(day.transactions)}</TableCell>
                                        <TableCell align="right">{formatCurrency(day.dispensed)}</TableCell>
                                        <TableCell align="right">{formatCurrency(day.surcharge)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                          {formatCurrency(day.total)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatNumber(location.totalTransactions)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(location.totalDispensed)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(location.totalSurcharge)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(location.totalAmount)}
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        ) : /* Settlement Report */ (
                          <Box sx={{ p: 2 }}>
                            {/* Per-location "Export Settlement" button removed (June 2026 feedback):
                                it repeated for every location and duplicated the single
                                "Export All Settlement" button at the top of the report. */}

                            {/* Daily Breakdown for Weekly/Monthly Settlement Reports */}
                            {(period === 'weekly' || period === 'monthly') && location.dailyData?.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                  Daily Settlement Breakdown
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell align="right"><strong>Transactions</strong></TableCell>
                                        <TableCell align="right"><strong>Settlement</strong></TableCell>
                                        <TableCell align="right"><strong>Withdrawals</strong></TableCell>
                                        <TableCell align="right"><strong>Surcharge</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {location.dailyData.map((day, idx) => (
                                        <TableRow key={day.date || idx} hover>
                                          <TableCell>{day.date}</TableCell>
                                          <TableCell align="right">{formatNumber(day.transactionCount || 0)}</TableCell>
                                          <TableCell align="right">{formatCurrency(day.settlementAmount || 0)}</TableCell>
                                          <TableCell align="right">{formatCurrency(day.withdrawalAmount || 0)}</TableCell>
                                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                                            {formatCurrency(day.surchargeAmount || 0)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Period Total</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                          {formatNumber(location.totalTransactions || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                          {formatCurrency(location.totalAmount || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                          {formatCurrency(location.withdrawalAmount || 0)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                          {formatCurrency(location.surchargeAmount || 0)}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                <Divider sx={{ my: 2 }} />
                              </Box>
                            )}

                            {/* Check if this is a PAI location (terminals start with GH) */}
                            {location.terminals?.some(t => typeof t === 'string' && t.startsWith('GH')) ? (
                              <>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                              Consolidated for all terminals
                              {location.vaultAccount && (
                                <Chip
                                  label={`Vault: ${formatAccountNumber(location.vaultAccount)}`}
                                  size="small"
                                  sx={{ ml: 2, fontWeight: 'medium' }}
                                  color="success"
                                />
                              )}
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6}>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">Terminals</Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {location.terminals?.map(terminal => (
                                      <Chip 
                                        key={terminal} 
                                        label={terminal} 
                                        size="small" 
                                        variant="outlined"
                                        color="primary"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                      <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                                      <Typography variant="h6">{formatNumber(location.totalTransactions)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="body2" color="text.secondary">Total Settlement</Typography>
                                      <Typography variant="h6" color="primary">{formatCurrency(location.totalAmount)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="body2" color="text.secondary">Withdrawals</Typography>
                                      <Typography variant="body1">{formatCurrency(location.withdrawalAmount)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="body2" color="text.secondary">Total Surcharge</Typography>
                                      <Typography variant="body1" color="success.main" fontWeight="medium">
                                        {formatCurrency(location.surchargeAmount)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              </Grid>
                            </Grid>
                              </>
                            ) : (
                              /* Non-PAI Settlement Report */
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                      <TableCell sx={{ width: 40 }} />
                                      <TableCell><strong>Terminal ID</strong></TableCell>
                                      <TableCell align="right"><strong>Transactions</strong></TableCell>
                                      <TableCell align="right"><strong>Settlement Amount</strong></TableCell>
                                      <TableCell align="center"><strong>Vault Account</strong></TableCell>
                                      <TableCell align="right"><strong>Withdrawal Amount</strong></TableCell>
                                      <TableCell align="right"><strong>Total Surcharge</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {location.transactions?.map((transaction, index) => {
                                      const physKey = `${location.name}:${transaction.terminalId}:${index}`;
                                      const physRows = transaction.physicalRows || [];
                                      const hasPhys = physRows.length > 0;
                                      const isExp = !!expandedPhysical[physKey];
                                      const bankSettled = physRows.reduce((s, p) => s + (p.settledAmount || 0), 0);
                                      const bankSurch = physRows.reduce((s, p) => s + (p.surchargeAmount || 0), 0);
                                      const bankTxns = physRows.reduce((s, p) => s + (p.txnCount || 0), 0);
                                      const physAtms = physRows.filter(p => p.attributed).length;
                                      return (
                                      <React.Fragment key={physKey}>
                                      <TableRow hover>
                                        <TableCell sx={{ width: 40 }}>
                                          {hasPhys && (
                                            <IconButton
                                              size="small"
                                              onClick={() => setExpandedPhysical(prev => ({ ...prev, [physKey]: !prev[physKey] }))}
                                              title="Per-physical bank-settled breakdown"
                                            >
                                              {isExp ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </IconButton>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={transaction.terminalId} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="right">{formatNumber(transaction.transactionCount)}</TableCell>
                                        <TableCell align="right">{formatCurrency(transaction.settlementAmount)}</TableCell>
                                        <TableCell align="center">
                                          {transaction.vaultAccount ? (
                                            <Chip
                                              label={formatAccountNumber(transaction.vaultAccount)}
                                              size="small"
                                              color="success"
                                              sx={{ fontWeight: 'medium' }}
                                            />
                                          ) : '-'}
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(transaction.withdrawalAmount)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                                          {formatCurrency(transaction.surchargeAmount)}
                                        </TableCell>
                                      </TableRow>
                                      {isExp && hasPhys && (
                                        <TableRow>
                                          <TableCell colSpan={7} sx={{ py: 1, backgroundColor: '#fafafa' }}>
                                            <Box sx={{ pl: 2 }}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                Settled to bank (settlement day): {formatCurrency(bankSettled)} · {formatNumber(bankTxns)} txns · {physAtms} ATM{physAtms !== 1 ? 's' : ''}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                Per-physical split of the actual bank deposit. The "Settlement Amount" above is the calendar-day captured total.
                                              </Typography>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell><strong>Physical ATM</strong></TableCell>
                                                    <TableCell align="right"><strong>Transactions</strong></TableCell>
                                                    <TableCell align="right"><strong>Settled</strong></TableCell>
                                                    <TableCell align="right"><strong>Surcharge</strong></TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {physRows.map((p, pi) => (
                                                    <TableRow key={pi}>
                                                      <TableCell>
                                                        {p.attributed ? (
                                                          <Chip label={p.physicalId} size="small" color="primary" variant="outlined" />
                                                        ) : (
                                                          <Chip label="Unattributed" size="small" color="warning" variant="outlined" />
                                                        )}
                                                      </TableCell>
                                                      <TableCell align="right">{formatNumber(p.txnCount)}</TableCell>
                                                      <TableCell align="right">{formatCurrency(p.settledAmount)}</TableCell>
                                                      <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(p.surchargeAmount)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                      </React.Fragment>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  </>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default SettlementReports;